import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Avatar,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import moment from 'moment';

import { TP_COLORS } from '../../../../../constants';
import { useAppSelector } from '../../../../../hooks';
import { checkUserPermission } from '../../../../../helper';

import { getAuthState } from '../../../../../store/AuthSlice';
import {
  ISection,
  deleteCourseResourceSectionByIdAsAdmin,
  getSectionsState,
} from '../../../../../store/V2/SectionSlice';
import ViewHomeworkModal from '../modals/ViewHomeworkModal';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../store/V2/RolesSlice';
import { getTenantsState } from '../../../../../store/V2/TenantsSlice';

interface HomeworkSectionItemProps {
  orderNumber: number;
  section: ISection;
  onEdit: (section: ISection) => void;
}

const buttonStyles: SxProps = {
  backgroundColor: '#3498DB',
  color: TP_COLORS.white,
  p: '8px',
  borderRadius: '5px',
  fontSize: '12px',
  fontWeight: 600,
};

const containerStyles: SxProps = {
  backgroundColor: 'transparent',
  borderRadius: '10px',
  border: '1px dashed #CBD5E1',
};

const HomeworkSectionItem: React.FC<HomeworkSectionItemProps> = ({
  orderNumber,
  section,
  onEdit,
}) => {
  const confirm = useConfirm();
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);
  const sectionsState = useAppSelector(getSectionsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  // State
  const [isViewHomeworkModalOpen, setViewHomeworkModalOpen] = useState(false);

  // Permissions
  const homeworkEditPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminHomework: [USER_PERMISSION_INDIVIDUALS.ADMIN_HOMEWORK_EDIT],
    },
  });

  const homeworkDeletePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminHomework: [USER_PERMISSION_INDIVIDUALS.ADMIN_HOMEWORK_DELETE],
    },
  });

  const dueDate = section.dueDate
    ? moment(section.dueDate).format('ddd MMM DD YYYY hh:mm A')
    : '';

  const handleDelete = () => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this homework?',
    }).then(() => {
      dispatch(deleteCourseResourceSectionByIdAsAdmin(section._id));
    });
  };

  return (
    <Card elevation={0} sx={containerStyles}>
      <CardContent sx={{ p: '16px' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" flexGrow={1} mr="15px">
            <Avatar
              sx={{
                width: '36px',
                height: '36px',
                backgroundColor: '#1C9FF7',
                mr: '15px',
              }}
            >
              {orderNumber}
            </Avatar>
            <Stack gap={1}>
              <Chip
                size="small"
                label="Homework"
                color="info"
                sx={{ width: 'fit-content' }}
              />
              <Typography
                variant="subtitle2"
                fontWeight={600}
                className="ellipsis-text one-line"
              >
                {section.title}
              </Typography>
              <Typography fontWeight={400} fontSize="14px" color="#1E293B">
                {dueDate}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center">
            <ButtonBase
              onClick={() => setViewHomeworkModalOpen(true)}
              sx={buttonStyles}
            >
              Homework
            </ButtonBase>

            {homeworkDeletePermission && (
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={handleDelete}
                  disabled={sectionsState.status === 'loading'}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}

            {homeworkEditPermission && (
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => onEdit(section)}
                  disabled={sectionsState.status === 'loading'}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </CardContent>
      {/* Modals */}
      {isViewHomeworkModalOpen && (
        <ViewHomeworkModal
          open={isViewHomeworkModalOpen}
          onClose={() => setViewHomeworkModalOpen(false)}
          section={section}
        />
      )}
    </Card>
  );
};

export default HomeworkSectionItem;
